import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { BACKUP } from "@/constants/testIds";
import { toast, confirmToast } from "@/lib/notify";
import { X, Download, Upload, AlertTriangle, Database, Clock, RotateCcw, Trash2, Play } from "lucide-react";

function formatBackupDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_) {
    return iso;
  }
}

function totalDocs(counts) {
  return Object.values(counts || {}).reduce((a, b) => a + b, 0);
}

export default function BackupModal({ open, onClose }) {
  const [downloading, setDownloading] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [replace, setReplace] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  const [autoBackups, setAutoBackups] = useState([]);
  const [autoBusy, setAutoBusy] = useState(false);
  const fileRef = useRef(null);

  const loadAutoBackups = async () => {
    try {
      const { data } = await api.get("/backups");
      setAutoBackups(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando respaldos automáticos");
    }
  };

  useEffect(() => {
    if (open) queueMicrotask(loadAutoBackups);
  }, [open]);

  if (!open) return null;

  const runBackupNow = async () => {
    setAutoBusy(true);
    try {
      await api.post("/backups/run");
      toast.success("Respaldo creado correctamente");
      await loadAutoBackups();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error creando respaldo");
    } finally {
      setAutoBusy(false);
    }
  };

  const downloadAuto = async (b) => {
    try {
      const res = await api.get(`/backups/${b.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ajar_backup_${(b.created_at || "").slice(0, 19).replace(/[:T]/g, "-")}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error descargando respaldo");
    }
  };

  const restoreAuto = async (b) => {
    const ok = await confirmToast(
      `¿Restaurar el respaldo del ${formatBackupDate(b.created_at)}?`,
      {
        description: "Los datos del respaldo se combinarán con los actuales (sin borrar nada).",
        confirmLabel: "Restaurar",
      },
    );
    if (!ok) return;
    setAutoBusy(true);
    try {
      const { data } = await api.post(`/backups/${b.id}/restore`, { replace: false });
      setSummary(data.summary || {});
      toast.success("Respaldo restaurado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error restaurando respaldo");
    } finally {
      setAutoBusy(false);
    }
  };

  const deleteAuto = async (b) => {
    const ok = await confirmToast(
      `¿Eliminar el respaldo del ${formatBackupDate(b.created_at)}?`,
      { confirmLabel: "Eliminar" },
    );
    if (!ok) return;
    try {
      await api.delete(`/backups/${b.id}`);
      setAutoBackups((prev) => prev.filter((x) => x.id !== b.id));
      toast.success("Respaldo eliminado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  const handleDownload = async () => {
    setError("");
    setDownloading(true);
    try {
      const { data } = await api.get("/backup");
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `ajar_backup_${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error descargando respaldo");
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadZip = async () => {
    setError("");
    setDownloadingZip(true);
    try {
      const res = await api.get("/backup.zip", { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `ajar_backup_${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error descargando ZIP");
    } finally {
      setDownloadingZip(false);
    }
  };

  const handleRestore = async () => {
    setError("");
    setSummary(null);
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setError("Selecciona un archivo JSON de respaldo");
      return;
    }
    setRestoring(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const collections = parsed.collections || parsed;
      const { data } = await api.post("/restore", { collections, replace });
      setSummary(data.summary || {});
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Error restaurando respaldo");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        data-testid={BACKUP.modal}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#f5f0e6] w-full max-w-2xl border border-[#d9cfb8] shadow-2xl"
        style={{ borderTop: "4px solid #c8a04a" }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#d9cfb8] bg-white">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#8b7e5e]">
              Sistema
            </div>
            <h2 className="font-serif text-2xl text-[#1c1a14]">Respaldo y Restauración</h2>
          </div>
          <button
            data-testid={BACKUP.closeButton}
            onClick={onClose}
            className="text-[#5d5849] hover:text-[#1c1a14]"
          >
            <X size={22} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Download */}
          <section className="border border-[#d9cfb8] bg-white p-5">
            <div className="flex items-start gap-3 mb-3">
              <Database size={18} className="text-[#1c2a3a] mt-0.5" />
              <div>
                <h3 className="font-serif text-lg text-[#1c1a14]">Descargar respaldo</h3>
                <p className="text-xs text-[#5d5849] leading-relaxed">
                  Exporta miembros, capturas, gastos y sobres en un único archivo JSON.
                  Guárdalo en un lugar seguro.
                </p>
              </div>
            </div>
            <button
              data-testid={BACKUP.downloadButton}
              onClick={handleDownload}
              disabled={downloading}
              className="inline-flex items-center gap-2 bg-[#1c2a3a] hover:bg-[#0f1a2c] text-[#e7c879] px-4 py-2.5 text-xs uppercase tracking-widest disabled:opacity-60"
            >
              <Download size={14} /> {downloading ? "Generando..." : "Descargar JSON"}
            </button>
            <button
              data-testid="backup-download-zip-button"
              onClick={handleDownloadZip}
              disabled={downloadingZip}
              className="ml-2 inline-flex items-center gap-2 bg-white border border-[#c8a04a] text-[#7a5b0f] hover:bg-[#fdf7e1] px-4 py-2.5 text-xs uppercase tracking-widest disabled:opacity-60"
            >
              <Download size={14} /> {downloadingZip ? "Generando..." : "Descargar ZIP (portátil)"}
            </button>
          </section>

          {/* Automatic backups */}
          <section data-testid={BACKUP.autoSection} className="border border-[#d9cfb8] bg-white p-5">
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-start gap-3">
                <Clock size={18} className="text-[#7a5b0f] mt-0.5" />
                <div>
                  <h3 className="font-serif text-lg text-[#1c1a14]">Respaldos automáticos</h3>
                  <p className="text-xs text-[#5d5849] leading-relaxed">
                    El sistema crea un respaldo <strong>diario automático</strong> (≈ 02:00 hora
                    CDMX) y conserva los últimos <strong>14</strong>. Puedes descargarlos,
                    restaurarlos o crear uno adicional ahora mismo.
                  </p>
                </div>
              </div>
              <button
                data-testid={BACKUP.autoRunNow}
                onClick={runBackupNow}
                disabled={autoBusy}
                className="inline-flex items-center gap-2 bg-[#1c2a3a] hover:bg-[#0f1a2c] text-[#e7c879] px-3 py-2 text-[11px] uppercase tracking-widest disabled:opacity-60"
              >
                <Play size={12} /> {autoBusy ? "Procesando..." : "Respaldar ahora"}
              </button>
            </div>

            {autoBackups.length === 0 ? (
              <div className="text-xs text-[#8b7e5e] border border-dashed border-[#d9cfb8] px-4 py-4 text-center">
                Aún no hay respaldos automáticos.
              </div>
            ) : (
              <div data-testid={BACKUP.autoList} className="border border-[#e8e1cd] divide-y divide-[#e8e1cd] max-h-52 overflow-y-auto">
                {autoBackups.map((b) => (
                  <div key={b.id} className="flex items-center gap-3 px-3 py-2 text-xs hover:bg-[#fbf7ee]">
                    <div className="flex-1 min-w-0">
                      <div className="text-[#1c1a14] tabular-nums">{formatBackupDate(b.created_at)}</div>
                      <div className="text-[10px] text-[#8b7e5e]">
                        {totalDocs(b.counts)} registros · {(b.size_bytes / 1024).toFixed(1)} KB
                        {b.trigger === "manual" ? " · manual" : " · automático"}
                      </div>
                    </div>
                    <button
                      data-testid={BACKUP.autoDownload(b.id)}
                      onClick={() => downloadAuto(b)}
                      title="Descargar ZIP"
                      className="text-[#1c2a3a] hover:text-[#c8a04a]"
                    >
                      <Download size={14} />
                    </button>
                    <button
                      data-testid={BACKUP.autoRestore(b.id)}
                      onClick={() => restoreAuto(b)}
                      disabled={autoBusy}
                      title="Restaurar (combinar con datos actuales)"
                      className="text-[#7a5b0f] hover:text-[#c8a04a] disabled:opacity-50"
                    >
                      <RotateCcw size={14} />
                    </button>
                    <button
                      data-testid={BACKUP.autoDelete(b.id)}
                      onClick={() => deleteAuto(b)}
                      title="Eliminar respaldo"
                      className="text-[#9b2c2c] hover:text-[#6e1f1f]"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Restore */}
          <section className="border border-[#d9cfb8] bg-white p-5">
            <div className="flex items-start gap-3 mb-3">
              <Upload size={18} className="text-[#7a5b0f] mt-0.5" />
              <div>
                <h3 className="font-serif text-lg text-[#1c1a14]">Restaurar respaldo</h3>
                <p className="text-xs text-[#5d5849] leading-relaxed">
                  Sube un archivo JSON previamente exportado. Por defecto se hace
                  <em> upsert</em> (combinar). Activa <strong>Reemplazar</strong> para borrar
                  los datos existentes antes de restaurar.
                </p>
              </div>
            </div>

            <input
              ref={fileRef}
              data-testid={BACKUP.fileInput}
              type="file"
              accept="application/json,.json"
              className="block w-full text-xs text-[#1c1a14] file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-[#1c2a3a] file:text-[#e7c879] file:text-[10px] file:uppercase file:tracking-widest hover:file:bg-[#0f1a2c]"
            />

            <label className="flex items-center gap-2 mt-3 text-xs text-[#5d5849] cursor-pointer">
              <input
                data-testid={BACKUP.replaceToggle}
                type="checkbox"
                checked={replace}
                onChange={(e) => setReplace(e.target.checked)}
                className="accent-[#c8a04a]"
              />
              <span>
                Reemplazar datos existentes
                {replace && (
                  <span className="ml-2 inline-flex items-center gap-1 text-[#a14040]">
                    <AlertTriangle size={12} /> destructivo
                  </span>
                )}
              </span>
            </label>

            <button
              data-testid={BACKUP.restoreButton}
              onClick={handleRestore}
              disabled={restoring}
              className="mt-4 inline-flex items-center gap-2 bg-white border border-[#c8a04a] text-[#7a5b0f] hover:bg-[#fdf7e1] px-4 py-2.5 text-xs uppercase tracking-widest disabled:opacity-60"
            >
              <Upload size={14} /> {restoring ? "Restaurando..." : "Restaurar"}
            </button>
          </section>

          {error && (
            <div
              data-testid={BACKUP.errorMsg}
              className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-xs"
            >
              {error}
            </div>
          )}

          {summary && (
            <div
              data-testid={BACKUP.summary}
              className="px-4 py-3 bg-[#fdf7e1] border border-[#e7c879] text-xs text-[#7a5b0f]"
            >
              <div className="font-medium mb-1">Restauración completada</div>
              <ul className="space-y-0.5">
                {Object.entries(summary).map(([col, info]) => (
                  <li key={col}>
                    <strong>{col}:</strong>{" "}
                    {info.skipped
                      ? "omitido"
                      : `${info.upserted} registros (${
                          info.replaced ? "reemplazado" : "combinado"
                        })`}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
